import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './MobileAuthForms.css';

// API base URL with intelligent detection for mobile testing - matches AuthContext logic
const getApiBaseUrl = () => {
  // Production: use environment variable
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_BASE_URL || 'https://knotcord.com';
  }
  
  // Development: intelligent detection
  const hostname = window.location.hostname;
  
  // Localhost or LAN IP: always use relative URL so setupProxy.js handles it
  // This avoids Safari CORS issues and doesn't require Django on 0.0.0.0
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.match(/^\d+\.\d+\.\d+\.\d+$/)) {
    return '';
  }
  
  // Fallback to environment variable
  return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

// Mobile Password Input Component
const MobilePasswordInput = ({ 
  name, 
  value, 
  onChange, 
  disabled, 
  placeholder, 
  isVisible,
  onToggleVisibility
}) => {
  return (
    <div className="mobile-password-input-container">
      <input
        type={isVisible ? "text" : "password"}
        name={name}
        value={value}
        onChange={onChange}
        disabled={disabled}
        className="mobile-form-input mobile-password-input"
        placeholder={placeholder}
      />
      <button
        type="button"
        onClick={onToggleVisibility}
        disabled={disabled}
        className="mobile-password-toggle-btn"
        title={isVisible ? "Hide password" : "Show password"}
      >
        {isVisible ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
            <line x1="1" y1="1" x2="23" y2="23"/>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        )}
      </button>
    </div>
  );
};

const MobileAuthForms = ({ onBack }) => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showEmailVerification, setShowEmailVerification] = useState(false);
  const [loginData, setLoginData] = useState({ identifier: '', password: '' });
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [verificationEmail, setVerificationEmail] = useState('');
  const [signUpData, setSignUpData] = useState({
    username: '', firstName: '', lastName: '', email: '', password: '', confirmPassword: '', referralSurvey: '', referralSurveyOther: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Password visibility states - only for sign in and sign up
  const [passwordVisibility, setPasswordVisibility] = useState({
    loginPassword: false,
    signUpPassword: false,
    signUpConfirmPassword: false
  });

  const { login, isAuthenticated, loading: authLoading } = useAuth();

  // If already authenticated, redirect to the intended destination immediately
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      const urlParams = new URLSearchParams(window.location.search);
      const intent = urlParams.get('intent');
      if (intent === 'newsrag') {
        navigate('/newsrag', { replace: true });
      } else if (intent === 'gravity') {
        navigate('/gravity', { replace: true });
      } else {
        window.location.replace('/mobile-home.html');
      }
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Toggle password visibility
  const togglePasswordVisibility = useCallback((field) => {
    setPasswordVisibility(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  }, []);

  // Form handlers
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSignUpChange = (e) => {
    const { name, value } = e.target;
    setSignUpData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleLoginSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/zoulang/account/login/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(loginData)
      });

      let data;
      try {
        data = await response.json();
      } catch (jsonError) {
        console.error('Failed to parse JSON response:', jsonError);
        setError('Invalid server response. Please try again.');
        return;
      }

      if (response.ok) {
        login(data.user);
        setLoginData({ identifier: '', password: '' });
        setSuccess('Login successful!');

        // Check if user came from trying to access gravity
        const urlParams = new URLSearchParams(window.location.search);
        const intent = urlParams.get('intent');

        // Show success message then redirect
        setTimeout(() => {
          if (intent === 'gravity') {
            navigate('/gravity');
          } else if (intent === 'newsrag') {
            navigate('/newsrag');
          } else {
            window.location.href = '/mobile-home.html';
          }
        }, 1500);
      } else {
        if (data.requires_verification) {
          setError('');
          setVerificationEmail(data.masked_email || loginData.identifier);
          setShowEmailVerification(true);
        } else {
          setError(data.error || 'Login failed.');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // More specific error messages
      if (err.message.includes('Failed to fetch') || err.message.includes('load failed')) {
        setError(`Cannot reach server at ${API_BASE_URL}. Make sure Django is running.`);
      } else if (err.message.includes('NetworkError')) {
        setError('Network connection failed. Check your connection.');
      } else if (err.message.includes('CORS')) {
        setError('CORS error. Backend may not be configured for this origin.');
      } else {
        setError(`Connection error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignUpSubmit = async () => {
    setLoading(true);
    setError('');

    if (signUpData.password !== signUpData.confirmPassword) {
      setError("Passwords don't match!");
      setLoading(false);
      return;
    }

    if (signUpData.password.length < 8) {
      setError("Password must be at least 8 characters!");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/zoulang/account/register/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify({
          username: signUpData.username,
          email: signUpData.email,
          password: signUpData.password,
          first_name: signUpData.firstName,
          last_name: signUpData.lastName,
          referral_survey: signUpData.referralSurvey === 'Others' ? signUpData.referralSurveyOther : signUpData.referralSurvey
        })
      });

      const data = await response.json();
      if (response.ok) {
        if (data.requires_verification) {
          setSuccess('Account created! Please check your email to verify.');
          setVerificationEmail(data.masked_email || signUpData.email);
          setShowEmailVerification(true);
          setIsLogin(true);
        } else {
          login(data.user);
          setSuccess('Account created successfully!');
          setSignUpData({
            username: '', firstName: '', lastName: '', email: '', password: '', confirmPassword: '', referralSurvey: '', referralSurveyOther: ''
          });

          // Check if user came from trying to access gravity
          const urlParams = new URLSearchParams(window.location.search);
          const intent = urlParams.get('intent');

          // Show success message then redirect
          setTimeout(() => {
            if (intent === 'gravity') {
              navigate('/gravity');
            } else if (intent === 'newsrag') {
              navigate('/newsrag');
            } else {
              window.location.href = '/mobile-home.html';
            }
          }, 1500);
        }
      } else {
        setError(data.error || 'Registration failed.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/zoulang/account/verify-email/send/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail })
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message || 'Verification email sent! Please check your email.');
      } else {
        setError(data.error || 'Failed to send verification email.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail.trim()) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/zoulang/account/password/reset/request/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail.trim().toLowerCase() })
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message || 'Password reset email sent! Check your email.');
        setForgotPasswordEmail('');
      } else {
        setError(data.error || 'Failed to send reset email.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mobile-auth-forms">
      {/* Back button to return to main page */}
      <button
        onClick={() => window.location.href = '/mobile-home.html'}
        className="mobile-auth-back-button"
      >
        ←
      </button>

      <div className="mobile-auth-content">
        <div className="mobile-auth-forms-container">
          {error && <div className="mobile-message mobile-error">{error}</div>}
          {success && <div className="mobile-message mobile-success">{success}</div>}

          {showEmailVerification ? (
            <div className="mobile-form-section">
              <h3>Email Verification Required</h3>
              <p>Please check your email (<strong>{verificationEmail}</strong>) and click the verification link to activate your account.</p>
              <p>After verifying your email, you can log in to your account.</p>
              <button 
                onClick={handleResendVerification} 
                disabled={loading} 
                className="mobile-submit-button"
              >
                {loading ? 'Sending...' : 'Resend Verification Email'}
              </button>
            </div>
          ) : showForgotPassword ? (
            <div className="mobile-form-section">
              <h3>Reset Your Password</h3>
              <p>Enter your email address and we'll send you a link to reset your password.</p>
              <div className="mobile-form-group">
                <label className="mobile-form-label">Email</label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  disabled={loading}
                  className="mobile-form-input"
                  placeholder="Enter your email address"
                />
              </div>
              <button 
                onClick={handleForgotPassword} 
                disabled={loading} 
                className="mobile-submit-button"
              >
                {loading ? 'Sending...' : 'Send Reset Email'}
              </button>
            </div>
          ) : (
            <>
              {/* Tab Switcher */}
              <div className="mobile-tab-switcher">
                <button
                  onClick={() => setIsLogin(true)}
                  className={`mobile-tab-button ${isLogin ? 'active' : ''}`}
                >
                  Sign In
                </button>
                <button
                  onClick={() => setIsLogin(false)}
                  className={`mobile-tab-button ${!isLogin ? 'active' : ''}`}
                >
                  Sign Up
                </button>
              </div>

              <div className="mobile-form-section">
                {isLogin ? (
                  // Login Form
                  <>
                    <div className="mobile-form-group">
                      <label className="mobile-form-label">Email or Username</label>
                      <input
                        type="text"
                        name="identifier"
                        value={loginData.identifier}
                        onChange={handleLoginChange}
                        disabled={loading}
                        className="mobile-form-input"
                        placeholder="Enter your email or username"
                      />
                    </div>
                    <div className="mobile-form-group">
                      <label className="mobile-form-label">Password</label>
                      <MobilePasswordInput
                        name="password"
                        value={loginData.password}
                        onChange={handleLoginChange}
                        disabled={loading}
                        placeholder="Enter your password"
                        isVisible={passwordVisibility.loginPassword}
                        onToggleVisibility={() => togglePasswordVisibility('loginPassword')}
                      />
                      <button
                        type="button"
                        onClick={() => setShowForgotPassword(true)}
                        disabled={loading}
                        className="mobile-forgot-link"
                      >
                        Forgot password?
                      </button>
                    </div>
                    <button 
                      onClick={handleLoginSubmit} 
                      disabled={loading} 
                      className="mobile-submit-button"
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                  </>
                ) : (
                  // Sign Up Form
                  <>
                    <div className="mobile-form-group">
                      <label className="mobile-form-label">Username</label>
                      <input
                        type="text"
                        name="username"
                        value={signUpData.username}
                        onChange={handleSignUpChange}
                        disabled={loading}
                        className="mobile-form-input"
                        placeholder="Choose a username"
                      />
                    </div>
                    <div className="mobile-form-row">
                      <div className="mobile-form-group">
                        <label className="mobile-form-label">First Name</label>
                        <input
                          type="text"
                          name="firstName"
                          value={signUpData.firstName}
                          onChange={handleSignUpChange}
                          disabled={loading}
                          className="mobile-form-input"
                          placeholder="First name"
                        />
                      </div>
                      <div className="mobile-form-group">
                        <label className="mobile-form-label">Last Name</label>
                        <input
                          type="text"
                          name="lastName"
                          value={signUpData.lastName}
                          onChange={handleSignUpChange}
                          disabled={loading}
                          className="mobile-form-input"
                          placeholder="Last name"
                        />
                      </div>
                    </div>
                    <div className="mobile-form-group">
                      <label className="mobile-form-label">Email</label>
                      <input
                        type="email"
                        name="email"
                        value={signUpData.email}
                        onChange={handleSignUpChange}
                        disabled={loading}
                        className="mobile-form-input"
                        placeholder="Enter your email"
                      />
                    </div>
                    <div className="mobile-form-group">
                      <label className="mobile-form-label">Password</label>
                      <MobilePasswordInput
                        name="password"
                        value={signUpData.password}
                        onChange={handleSignUpChange}
                        disabled={loading}
                        placeholder="Create a password"
                        isVisible={passwordVisibility.signUpPassword}
                        onToggleVisibility={() => togglePasswordVisibility('signUpPassword')}
                      />
                    </div>
                    <div className="mobile-form-group">
                      <label className="mobile-form-label">Confirm Password</label>
                      <MobilePasswordInput
                        name="confirmPassword"
                        value={signUpData.confirmPassword}
                        onChange={handleSignUpChange}
                        disabled={loading}
                        placeholder="Confirm your password"
                        isVisible={passwordVisibility.signUpConfirmPassword}
                        onToggleVisibility={() => togglePasswordVisibility('signUpConfirmPassword')}
                      />
                    </div>
                    <div className="mobile-form-group">
                      <label className="mobile-form-label">How did you find us?</label>
                      <select
                        name="referralSurvey"
                        value={signUpData.referralSurvey}
                        onChange={handleSignUpChange}
                        disabled={loading}
                        className="mobile-form-input"
                      >
                        <option value="">Select an option</option>
                        <option value="Google">Google</option>
                        <option value="X (Twitter)">X (Twitter)</option>
                        <option value="Reddit">Reddit</option>
                        <option value="LinkedIn">LinkedIn</option>
                        <option value="Friends">Friends</option>
                        <option value="Others">Others</option>
                      </select>
                      {signUpData.referralSurvey === 'Others' && (
                        <input
                          type="text"
                          name="referralSurveyOther"
                          value={signUpData.referralSurveyOther}
                          onChange={handleSignUpChange}
                          disabled={loading}
                          className="mobile-form-input"
                          placeholder="Please specify..."
                          style={{ marginTop: '10px' }}
                        />
                      )}
                    </div>
                    <button
                      onClick={handleSignUpSubmit}
                      disabled={loading}
                      className="mobile-submit-button"
                    >
                      {loading ? 'Creating Account...' : 'Sign Up'}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileAuthForms;